import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { formatDateTime } from "../shared/format";
import { concernStatuses, type ConcernReport, type ConcernStatus } from "./types";
import "./concerns.css";

export default function ConcernDetailsPage() {
  const { reportId = "" } = useParams();
  const location = useLocation();
  const from = typeof location.state?.from === "string" && location.state.from.startsWith("/concerns?") ? location.state.from : "/concerns";
  const query = useQuery({ queryKey: ["concern", reportId], queryFn: () => api.getConcern(reportId) });
  return <div className="pa-page-stack pa-concerns">
    <Link className="pa-detail-link" to={from}><ArrowLeft size={16} aria-hidden="true" /> All reported concerns</Link>
    <div className="pa-title-row"><div><h1>Review concern</h1><p>Only authorized Coprint operations staff can access this report.</p></div></div>
    {query.isPending ? <p role="status">Loading report…</p> : null}
    {query.isError ? <div className="pa-form-error" role="alert">{query.error.message} <button className="pa-secondary-button" onClick={() => void query.refetch()}>Retry</button></div> : null}
    {query.data ? <ReportDetail key={query.data.report_id} report={query.data} /> : null}
  </div>;
}

function ReportDetail({ report }: { report: ConcernReport }) {
  const client = useQueryClient();
  const [status, setStatus] = useState<ConcernStatus>(report.status);
  const [notes, setNotes] = useState(report.review_notes ?? "");
  const [validation, setValidation] = useState("");
  const [saved, setSaved] = useState(false);
  const update = useMutation({
    retry: false,
    mutationFn: () => api.reviewConcern(report.report_id, status, notes.trim(), report.version),
    onSuccess(data) {
      client.setQueryData(["concern", report.report_id], data);
      void client.invalidateQueries({ queryKey: ["concerns"] });
      setSaved(true);
    }
  });
  return <div className="pa-concern-detail-grid">
    <section className="pa-panel pa-concern-details">
      <div className="pa-concern-heading"><h2>Report details</h2><span className={`pa-concern-status pa-concern-status-${report.status.toLowerCase()}`}>{concernStatuses[report.status]}</span></div>
      <dl className="pa-concern-facts">
        <div><dt>Reference</dt><dd>{report.report_id}</dd></div>
        <div><dt>Submitted</dt><dd>{formatDateTime(report.created_at)}</dd></div>
        <div><dt>Reporter</dt><dd>{report.reporter_user_id ? <Link to={`/users/${report.reporter_user_id}`}>{report.reporter_name}</Link> : report.reporter_name}</dd></div>
        <div><dt>Reporter’s workspace</dt><dd>{report.organization_id ? <Link to={`/organizations/${report.organization_id}`}>View organization</Link> : "Not provided"}</dd></div>
        <div><dt>Reported person</dt><dd>{report.reported_user_name || "Not provided"}</dd></div>
        <div><dt>Project reference</dt><dd>{report.project_reference || "Not provided"}</dd></div>
        <div><dt>Task reference</dt><dd>{report.task_reference || "Not provided"}</dd></div>
      </dl>
      <h3>What happened</h3><p className="pa-concern-description">{report.description}</p>
      <h3>Screenshot</h3>
      {report.screenshot ? <a className="pa-detail-link" href={report.screenshot.download_url} target="_blank" rel="noreferrer">{report.screenshot.file_name}<ExternalLink size={16} aria-hidden="true" /></a> : <p>No screenshot attached.</p>}
      <p className="pa-concern-help">Names and references above were entered by the reporter. Verify the context before taking action.</p>
    </section>
    <section className="pa-panel pa-concern-review">
      <h2>Review</h2><p>Record your investigation and any action taken. Updating this report does not automatically remove content or change user access.</p>
      <form onSubmit={event => { event.preventDefault(); setSaved(false); if (!notes.trim()) { setValidation("Add review notes before saving."); return; } setValidation(""); update.mutate(); }}>
        <label>Review status<select value={status} disabled={update.isPending} onChange={event => setStatus(event.target.value as ConcernStatus)}>
          {Object.entries(concernStatuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <label>Internal review notes (required)<textarea rows={8} maxLength={5000} value={notes} disabled={update.isPending} onChange={event => setNotes(event.target.value)} placeholder="Investigation, follow-up and actions taken" /></label>
        <p className="pa-concern-help">These notes are visible to the Coprint operations team.</p>
        {validation ? <p className="pa-form-error" role="alert">{validation}</p> : null}
        {update.isError ? <div className="pa-form-error" role="alert">{update.error.message}<button type="button" className="pa-secondary-button" onClick={() => void client.invalidateQueries({ queryKey: ["concern", report.report_id] })}>Refresh report</button></div> : null}
        {saved ? <p role="status">Review saved.</p> : null}
        <button type="submit" className="pa-primary-button" disabled={update.isPending}>{update.isPending ? "Saving…" : "Save review"}</button>
      </form>
      <p className="pa-concern-help">Last updated {formatDateTime(report.updated_at)}</p>
    </section>
  </div>;
}
